import React, { useEffect, useRef, useState } from 'react';

import { gymAPI } from '../lib/api';
import { useRabel } from './gymRabel';
import { BTN } from './Gym';

/**
 * The gym check-in code: each person's own membership QR, saved once as a
 * picture (a screenshot from the gym's app, or a photo) and shown full screen
 * on white at the door, with the screen kept awake while it's up.
 *
 * Where the phone can read QR codes (Chrome on Android), the picture is cropped
 * to just the code so it fills the screen; elsewhere it's kept whole.
 */

const MAX_SIDE = 1000;

/** Read a file as an image element. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Not a picture')); };
    img.src = url;
  });
}

/** The code's box in the picture, with a quiet margin, if this phone can find it. */
async function findCode(img) {
  try {
    if (!('BarcodeDetector' in window)) return null;
    const formats = await window.BarcodeDetector.getSupportedFormats();
    if (!formats.includes('qr_code')) return null;
    const [code] = await new window.BarcodeDetector({ formats: ['qr_code'] }).detect(img);
    if (!code) return null;
    const { x, y, width, height } = code.boundingBox;
    const pad = Math.max(width, height) * 0.12;
    const left = Math.max(0, x - pad);
    const top = Math.max(0, y - pad);
    return { x: left, y: top, w: Math.min(img.naturalWidth, x + width + pad) - left, h: Math.min(img.naturalHeight, y + height + pad) - top };
  } catch {
    return null;
  }
}

/** The picture, cropped to the code where possible and shrunk to a sensible size, as a data URL. */
async function prepare(file) {
  const img = await loadImage(file);
  const box = (await findCode(img)) || { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight };
  const scale = Math.min(1, MAX_SIDE / Math.max(box.w, box.h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(box.w * scale);
  canvas.height = Math.round(box.h * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // Sharp edges: a QR code scales best without smoothing.
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, canvas.width, canvas.height);
  // PNG keeps a code's edges crisp; a big photo goes to JPEG to stay small.
  const png = canvas.toDataURL('image/png');
  return png.length < 1_500_000 ? png : canvas.toDataURL('image/jpeg', 0.9);
}

/** Keep the screen on while the code is up. */
function useWakeLock(on) {
  useEffect(() => {
    if (!on || !navigator.wakeLock) return undefined;
    let lock = null;
    let gone = false;
    const take = () => navigator.wakeLock.request('screen').then(l => { if (gone) l.release(); else lock = l; }).catch(() => {});
    take();
    const again = () => { if (document.visibilityState === 'visible') take(); };
    document.addEventListener('visibilitychange', again);
    return () => { gone = true; document.removeEventListener('visibilitychange', again); lock?.release().catch(() => {}); };
  }, [on]);
}

/** The "Check in" button on the home screen, and the code it shows. */
export function CheckIn() {
  const { say } = useRabel();
  const [code, setCode] = useState(undefined); // undefined loading, null none yet
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const picker = useRef(null);
  useWakeLock(open && !!code);

  useEffect(() => { gymAPI.checkin().then(r => setCode(r.data)).catch(() => setCode(null)); }, []);

  async function choose(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const image = await prepare(file);
      const r = await gymAPI.saveCheckin(image);
      setCode(r.data);
      setOpen(true);
    } catch (err) {
      setError(err.response ? 'Couldn\'t save it. Try again.' : 'That doesn\'t look like a picture.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Remove your check-in code?')) return;
    await gymAPI.deleteCheckin().catch(() => {});
    setCode(null);
    setOpen(false);
  }

  const input = <input ref={picker} type="file" accept="image/*" className="hidden" onChange={choose} />;

  if (code === undefined) return null;

  return (
    <>
      {input}
      <button
        onClick={() => (code ? setOpen(true) : picker.current?.click())}
        disabled={busy}
        className={`${BTN} w-full mb-4 flex items-center justify-center gap-2 bg-[#141414] border border-[#2a2a2a] text-white`}
      >
        <QrIcon />
        {busy ? 'Saving…' : code ? say('Check in', 'Check in, babe') : 'Add your gym check-in code'}
      </button>
      {error && <p className="text-sm text-red-400 -mt-2 mb-3">{error}</p>}

      {open && code && (
        <div className="fixed inset-0 z-50 bg-white text-black flex flex-col" role="dialog" aria-label="Check-in code">
          <div className="flex-1 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
            <img src={code.image} alt="Your gym check-in code" className="w-[min(60vw,260px)] max-h-[50vh] object-contain [image-rendering:pixelated]" />
          </div>
          <p className="text-center text-sm text-gray-500 px-6">Turn your brightness up if it won't scan.</p>
          <div className="flex gap-2 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button onClick={() => picker.current?.click()} className={`${BTN} flex-1 bg-gray-100 text-gray-800 text-sm`}>Replace</button>
            <button onClick={remove} className={`${BTN} bg-gray-100 text-red-600 text-sm px-4`}>Remove</button>
            <button onClick={() => setOpen(false)} className={`${BTN} flex-1 bg-black text-white`}>Done</button>
          </div>
        </div>
      )}
    </>
  );
}

function QrIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />
    </svg>
  );
}
